/**
 * Centralized route path patterns
 * Single source of truth for all route definitions across the app
 * Used by App.jsx, Link.jsx, and breadcrumbRouteHelpers.js
 */

// Auth routes
export const LOGIN_CALLBACK = '/login/callback';
export const LOGIN_SSO_REDIRECT = '/sso';
export const FORGOT_PASSWORD = '/forgot-password';
export const RESET_PASSWORD = '/reset-password';
export const ACCEPT_INVITATION = '/accept-invitation';
export const INDEX = '/';
export const NOT_FOUND = '/:slug/_not-found';

// Project routes
export const PROJECTS_INDEX = '/:slug/projects';
export const PROJECTS_INITIATIVES = '/:slug/projects/:projectId/initiatives';
export const PROJECTS_ARCHIVED = '/:slug/projects/archived';
export const PROJECTS_NEW = '/:slug/projects/new';
export const PROJECTS_SHOW = '/:slug/projects/:projectId';
export const PROJECTS_EDIT = '/:slug/projects/:projectId/edit';
export const PROJECTS_FILES = '/:slug/projects/:projectId/files';

// Change Impact routes
export const CHANGE_IMPACTS_INDEX = '/:slug/projects/:projectId/change-impacts';
export const CHANGE_IMPACTS_NEW =
  '/:slug/projects/:projectId/change-impacts/new';
export const CHANGE_IMPACTS_DATA_IMPORT =
  '/:slug/projects/:projectId/change-impacts/data-import';
export const CHANGE_IMPACTS_SHOW =
  '/:slug/projects/:projectId/change-impacts/:changeImpactId';
export const CHANGE_IMPACTS_EDIT =
  '/:slug/projects/:projectId/change-impacts/:changeImpactId/edit';

// Task routes
export const TASKS_NEW =
  '/:slug/projects/:projectId/change-impacts/:changeImpactId/tasks/new';
export const TASKS_SHOW =
  '/:slug/projects/:projectId/change-impacts/:changeImpactId/tasks/:taskId';
export const TASKS_EDIT =
  '/:slug/projects/:projectId/change-impacts/:changeImpactId/tasks/:taskId/edit';

// Stakeholder routes
export const STAKEHOLDERS_INDEX = '/:slug/projects/:projectId/stakeholders';
export const STAKEHOLDERS_SETTINGS =
  '/:slug/projects/:projectId/stakeholders/settings';
export const STAKEHOLDERS_SHOW =
  '/:slug/projects/:projectId/stakeholders/:stakeholderId';
export const STAKEHOLDERS_NEW_STEP_STAKEHOLDER =
  '/:slug/projects/:projectId/stakeholders/new/step-stakeholder';
export const STAKEHOLDERS_NEW_STEP_INFORMATION =
  '/:slug/projects/:projectId/stakeholders/new/step-information';
export const STAKEHOLDERS_NEW_STEP_GROUP_PARTICIPANTS =
  '/:slug/projects/:projectId/stakeholders/new/step-group-participants';
export const STAKEHOLDERS_NEW_STEP_COMMUNICATORS =
  '/:slug/projects/:projectId/stakeholders/new/step-communicators';
export const STAKEHOLDERS_EDIT_STEP_STAKEHOLDER =
  '/:slug/projects/:projectId/stakeholders/:stakeholderId/edit/step-stakeholder';
export const STAKEHOLDERS_EDIT_STEP_INFORMATION =
  '/:slug/projects/:projectId/stakeholders/:stakeholderId/edit/step-information';
export const STAKEHOLDERS_EDIT_STEP_GROUP_PARTICIPANTS =
  '/:slug/projects/:projectId/stakeholders/:stakeholderId/edit/step-group-participants';
export const STAKEHOLDERS_EDIT_STEP_COMMUNICATORS =
  '/:slug/projects/:projectId/stakeholders/:stakeholderId/edit/step-communicators';

// Change Strategy routes
export const CHANGE_STRATEGY_INDEX =
  '/:slug/projects/:projectId/change-strategy';
export const CHANGE_STRATEGY_SETTINGS =
  '/:slug/projects/:projectId/change-strategy/settings';
export const CHANGE_STRATEGY_NEW_STEP_GENERAL =
  '/:slug/projects/:projectId/change-strategy/new/step-general';
export const CHANGE_STRATEGY_NEW_STEP_DETAILS =
  '/:slug/projects/:projectId/change-strategy/new/step-details';
export const CHANGE_STRATEGY_NEW_STEP_GOVERNANCE =
  '/:slug/projects/:projectId/change-strategy/new/step-governance';
export const CHANGE_STRATEGY_NEW_STEP_MILESTONES =
  '/:slug/projects/:projectId/change-strategy/new/step-milestones';
export const CHANGE_STRATEGY_NEW_STEP_LINKED_FILES =
  '/:slug/projects/:projectId/change-strategy/new/step-linked-files';
export const CHANGE_STRATEGY_EDIT_STEP_GENERAL =
  '/:slug/projects/:projectId/change-strategy/edit/step-general';
export const CHANGE_STRATEGY_EDIT_STEP_DETAILS =
  '/:slug/projects/:projectId/change-strategy/edit/step-details';
export const CHANGE_STRATEGY_EDIT_STEP_GOVERNANCE =
  '/:slug/projects/:projectId/change-strategy/edit/step-governance';
export const CHANGE_STRATEGY_EDIT_STEP_MILESTONES =
  '/:slug/projects/:projectId/change-strategy/edit/step-milestones';
export const CHANGE_STRATEGY_EDIT_STEP_LINKED_FILES =
  '/:slug/projects/:projectId/change-strategy/edit/step-linked-files';

// Portfolio routes
export const PARENT_PORTFOLIO_NEW = '/:slug/parent-portfolio/new';
export const PARENT_PORTFOLIO_EDIT =
  '/:slug/parent-portfolio/:parentPortfolioId/edit';
export const PORTFOLIO_INFO = '/:slug/portfolio/:portfolioId/info';
export const PORTFOLIO_PROJECTS = '/:slug/portfolio/:portfolioId/projects';
export const PORTFOLIO_SETTINGS = '/:slug/portfolio/:portfolioId/settings';
export const PORTFOLIO_NEW_STEP_GENERAL = '/:slug/portfolio/new/step-general';
export const PORTFOLIO_NEW_STEP_DETAILS = '/:slug/portfolio/new/step-details';
export const PORTFOLIO_NEW_STEP_MILESTONES =
  '/:slug/portfolio/new/step-milestones';
export const PORTFOLIO_NEW_STEP_LINKED_FILES =
  '/:slug/portfolio/new/step-linked-files';
export const PORTFOLIO_EDIT_STEP_GENERAL =
  '/:slug/portfolio/:portfolioId/edit/step-general';
export const PORTFOLIO_EDIT_STEP_DETAILS =
  '/:slug/portfolio/:portfolioId/edit/step-details';
export const PORTFOLIO_EDIT_STEP_MILESTONES =
  '/:slug/portfolio/:portfolioId/edit/step-milestones';
export const PORTFOLIO_EDIT_STEP_LINKED_FILES =
  '/:slug/portfolio/:portfolioId/edit/step-linked-files';

// Initiative routes (with type patterns)
export const INITIATIVES_NEW = '/:slug/projects/:projectId/:initiativeType/new';
export const INITIATIVES_SHOW = '/:slug/:initiativeType/:initiativeId';
export const INITIATIVES_PREVIEW =
  '/:slug/:initiativeType/:initiativeId/preview';
export const INITIATIVES_EDIT = '/:slug/:initiativeType/:initiativeId/edit';
export const INITIATIVES_FILES = '/:slug/:initiativeType/:initiativeId/files';

// File routes
export const FILES_NEW = '/:slug/projects/:projectId/files/new';
export const FILES_EDIT = '/:slug/files/:fileId/edit';

// Employee routes
export const EMPLOYEES_SHOW = '/:slug/employees/:employeeId';
export const EMPLOYEES_CHANGE_IMPACTS =
  '/:slug/employees/:employeeId/change-impacts';
export const EMPLOYEES_TASKS = '/:slug/employees/:employeeId/tasks';
export const EMPLOYEES_STAKEHOLDER_PROFILES =
  '/:slug/employees/:employeeId/stakeholder-profiles';

// Report routes
export const PROJECT_REPORTS = '/:slug/project-reports';
export const CHANGE_IMPACT_REPORTS = '/:slug/change-impact-reports';

// Organization routes
export const ORGANIZATION_SHOW = '/:slug/organization';
export const ORGANIZATION_EDIT = '/:slug/organization/edit';
export const ORGANIZATION_CUSTOM_SETTINGS =
  '/:slug/organization/custom-settings';
export const ORGANIZATION_STAKEHOLDERS = '/:slug/organization/stakeholders';
export const ORGANIZATION_STAKEHOLDERS_NEW =
  '/:slug/organization/stakeholders/new';
export const ORGANIZATION_USERS = '/:slug/organization/users';
export const ORGANIZATION_USERS_NEW = '/:slug/organization/users/new';
export const ORGANIZATION_USERS_SHOW = '/:slug/organization/users/:userId';
export const ORGANIZATION_USERS_EDIT = '/:slug/organization/users/:userId/edit';
export const ORGANIZATION_USERS_PERMISSIONS_SHOW =
  '/:slug/organization/users/:userId/permissions';
export const ORGANIZATION_USERS_PERMISSIONS_NEW =
  '/:slug/organization/users/:userId/permissions/new';
export const ORGANIZATION_USERS_PERMISSIONS_EDIT =
  '/:slug/organization/users/:userId/permissions/:projectId/edit';
export const ORGANIZATION_EMPLOYEES = '/:slug/organization/employees';
export const ORGANIZATION_EMPLOYEES_EDIT = '/:slug/organization/employees/edit';
export const ORGANIZATION_CUSTOM_SETTINGS_STATUSES_EDIT =
  '/:slug/organization/custom-settings/statuses/edit';
export const ORGANIZATION_CUSTOM_SETTINGS_CATEGORIES_EDIT =
  '/:slug/organization/custom-settings/categories/edit';
export const ORGANIZATION_CUSTOM_SETTINGS_CHANGE_IMPACT_TYPES_EDIT =
  '/:slug/organization/custom-settings/change-impact-types/edit';
export const ORGANIZATION_CUSTOM_SETTINGS_ORGANIZATION_SETTINGS_EDIT =
  '/:slug/organization/custom-settings/organization-settings/edit';

// Change Insights routes
export const CHANGE_INSIGHTS_STUDIO_SHOW =
  '/:slug/:initiativeType/:initiativeId/change-insights';
export const CHANGE_INSIGHTS_STUDIO_SETTINGS =
  '/:slug/:initiativeType/:initiativeId/change-insights/settings';
export const CHANGE_INSIGHTS_STUDIO_RESPONDENT_ANSWERS =
  '/:slug/:initiativeType/:initiativeId/change-insights/:surveyId/results/:responseId';
export const CHANGE_INSIGHTS_STUDIO_PREVIEW =
  '/:slug/organization/custom-settings/change-insights/template/:templateId';
export const CHANGE_INSIGHTS_NEW =
  '/:slug/:initiativeType/:initiativeId/change-insights/new';
export const CHANGE_INSIGHTS_EDIT =
  '/:slug/:initiativeType/:initiativeId/change-insights/edit';
export const CHANGE_INSIGHTS_ANALYSIS_SHOW =
  '/:slug/:initiativeType/:initiativeId/change-insights-analysis';
export const ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_NEW =
  '/:slug/organization/custom-settings/change-insights/new';
export const ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_EDIT =
  '/:slug/organization/custom-settings/change-insights/edit/:templateId';
export const ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_NEW =
  '/:slug/organization/custom-settings/change-insights/preview/new';
export const ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_EDIT =
  '/:slug/organization/custom-settings/change-insights/preview/edit/:templateId';

// Survey routes
export const SURVEY = '/:slug/change-insights/surveys/:surveyId';
export const SURVEY_RESPONSE =
  '/:slug/change-insights/surveys/:surveyId/response/:responseId';
export const SURVEY_SUBMIT_EMAIL = '/survey-submit-email';

// Other routes
export const ONLINE_EVENTS = '/online-events';
export const THANK_YOU = '/thank-you';
export const AI_ASSISTANT = '/:slug/ai-assistant';

// Helper patterns for matching (used in breadcrumbRouteHelpers.js)
export const PROJECTS_PAGE_PATTERN = '/:slug/projects/:projectId/:page';
export const PROJECTS_FILTER_PATTERN = '/:slug/projects/:filter';
